import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";

// ─── Styles ───────────────────────────────────────────────────────────────────
const Container = styled.div`
  position: relative;
`;

const InputRow = styled.div`
  display: flex;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  background: #fff;
  min-height: 36px;
  transition: border-color 0.15s, box-shadow 0.15s;
  ${({ $open }) => $open && `
    border-color: #3f88a5;
    box-shadow: 0 0 0 3px rgba(63,136,165,0.08);
  `}
`;

const TagStrip = styled.div`
  flex: 1;
  display: flex;
  gap: 5px;
  padding: 4px 8px;
  overflow-x: auto;
  align-items: center;
  /* Always show horizontal scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;

const Placeholder = styled.span`
  color: #94a3b8;
  font-size: 13px;
  white-space: nowrap;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 20px;
  padding: 2px 8px 2px 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TagRemove = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #0369a1;
  font-size: 15px;
  line-height: 1;
  display: flex;
  align-items: center;
  opacity: 0.7;
  &:hover { opacity: 1; }
`;

const ToggleBtn = styled.button`
  padding: 0 10px;
  background: none;
  border: none;
  border-left: 1px solid #f1f5f9;
  cursor: pointer;
  color: #64748b;
  font-size: 12px;
  flex-shrink: 0;
  transition: color 0.15s;
  &:hover { color: #3f88a5; }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  overflow-y: scroll;          /* always visible scrollbar */
  max-height: 200px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  /* scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;

const SearchBox = styled.div`
  padding: 6px 8px;
  border-bottom: 1px solid #f1f5f9;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 5px 8px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
  color: #1e293b;
  &:focus { border-color: #3f88a5; }
`;

const DropdownItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  font-size: 13px;
  color: ${({ $selectAll }) => ($selectAll ? "#3f88a5" : "#1e293b")};
  font-weight: ${({ $selectAll }) => ($selectAll ? 600 : 400)};
  cursor: pointer;
  border-bottom: ${({ $selectAll }) => ($selectAll ? "1px solid #f1f5f9" : "none")};
  &:hover { background: #f8fafc; }
  input[type="checkbox"] {
    accent-color: #3f88a5;
    width: 14px;
    height: 14px;
    cursor: pointer;
    flex-shrink: 0;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
const TagMultiSelect = ({
  options = [],
  selected = [],       // array of keys
  getKey,
  getLabel,
  onToggle,
  onSelectAll,
  placeholder = "Select…",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const saRef = useRef(null);

  const filtered = search.trim()
    ? options.filter(o => getLabel(o).toLowerCase().includes(search.toLowerCase()))
    : options;

  const allSelected = options.length > 0 && options.every(o => selected.includes(getKey(o)));
  const someSelected = options.some(o => selected.includes(getKey(o)));

  if (saRef.current) {
    saRef.current.indeterminate = someSelected && !allSelected;
  }

  const selectedOptions = options.filter(o => selected.includes(getKey(o)));

  const handleOpen = useCallback(() => {
    setOpen(v => {
      if (!v) setTimeout(() => searchRef.current?.focus(), 0);
      else setSearch("");
      return !v;
    });
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <Container ref={containerRef}>
      <InputRow $open={open} onClick={handleOpen} style={{ cursor: "pointer" }}>
        <TagStrip>
          {selectedOptions.length === 0
            ? <Placeholder>{placeholder}</Placeholder>
            : selectedOptions.map(o => (
                <Tag key={getKey(o)}>
                  {getLabel(o)}
                  <TagRemove
                    type="button"
                    onClick={e => { e.stopPropagation(); onToggle(getKey(o)); }}
                  >
                    ×
                  </TagRemove>
                </Tag>
              ))
          }
        </TagStrip>
        <ToggleBtn type="button" onClick={e => e.stopPropagation()}>
          {open ? "▴" : "▾"}
        </ToggleBtn>
      </InputRow>

      {open && (
        <Dropdown>
          <SearchBox>
            <SearchInput
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              placeholder="Search…"
            />
          </SearchBox>
          <DropdownItem $selectAll>
            <input
              type="checkbox"
              ref={saRef}
              checked={allSelected}
              onChange={() => onSelectAll(allSelected ? [] : options.map(getKey))}
            />
            Select All
          </DropdownItem>
          {filtered.map(o => (
            <DropdownItem key={getKey(o)}>
              <input
                type="checkbox"
                checked={selected.includes(getKey(o))}
                onChange={() => onToggle(getKey(o))}
              />
              {getLabel(o)}
            </DropdownItem>
          ))}
          {filtered.length === 0 && (
            <DropdownItem as="div" style={{ color: "#94a3b8", fontStyle: "italic", cursor: "default" }}>
              No results
            </DropdownItem>
          )}
        </Dropdown>
      )}
    </Container>
  );
};

export default TagMultiSelect;
